import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ShieldCheck, Clock, CheckCircle2, AlertTriangle, Building2 } from 'lucide-react';
import { formatCurrency } from '../lib/utils';

export default function TransferConfirmation({ open, onClose, transferDetails }) {
  if (!transferDetails) return null;

  const {
    type = 'Transfer',
    amount = 0,
    recipient = '',
    fromAccount = '',
    currency = 'USD',
    convertedAmount,
    fxRate,
    targetCurrency,
  } = transferDetails;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex flex-col items-center text-center pt-2">
            <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center mb-4">
              <CheckCircle2 className="h-8 w-8 text-green-600" />
            </div>
            <DialogTitle className="text-xl" style={{ fontFamily: 'Space Grotesk' }}>
              {type} Submitted
            </DialogTitle>
          </div>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Amount */}
          <div className="text-center">
            <p className="text-3xl font-bold tabular-nums" style={{ fontFamily: 'Space Grotesk' }}>
              {formatCurrency(amount)}
            </p>
            {convertedAmount && targetCurrency && (
              <p className="text-sm text-muted-foreground mt-1">
                ≈ {formatCurrency(convertedAmount)} {targetCurrency} at rate {fxRate}
              </p>
            )}
          </div>

          {/* Details */}
          <div className="bg-gray-50 rounded-xl p-4 space-y-2">
            {recipient && (
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">To</span>
                <span className="font-medium">{recipient}</span>
              </div>
            )}
            {fromAccount && (
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">From</span>
                <span className="font-medium">{fromAccount}</span>
              </div>
            )}
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Status</span>
              <Badge className="bg-yellow-50 text-yellow-700 border-yellow-200 text-xs">Processing</Badge>
            </div>
          </div>

          {/* FDIC Notice */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                <Clock className="h-4 w-4 text-[#0B5CAB]" />
              </div>
              <div>
                <p className="text-sm font-semibold text-[#0B5CAB]">Processing Time</p>
                <p className="text-sm text-blue-800 mt-1">
                  This transaction will take <strong>3–5 business days</strong> to complete.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                <ShieldCheck className="h-4 w-4 text-amber-700" />
              </div>
              <div>
                <p className="text-sm font-semibold text-amber-800">FDIC Insurance Hold</p>
                <p className="text-sm text-amber-700 mt-1">
                  Funds may be held for <strong>FDIC insurance verification purposes</strong>. This is a standard procedure to protect your deposit and ensure compliance with federal regulations.
                </p>
              </div>
            </div>
          </div>

          {/* Security badge */}
          <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
            <Building2 className="h-3 w-3" />
            <span>Mega Millions Reserve Escrow Bank · FDIC Insured · Member FDIC</span>
          </div>
        </div>

        <DialogFooter>
          <Button onClick={() => onClose(false)} className="w-full" data-testid="transfer-confirmation-close">
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
